import "./Dashboard.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL || "http://127.0.0.1:5000";

const MONTHS = [
  "Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor",
  "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from(
  { length: CURRENT_YEAR - 2024 + 2 },
  (_, i) => 2024 + i
);

const getCurrentMonth = () => MONTHS[new Date().getMonth()];

const getApartmentNumber = (a) => {
  const v = a?.name ?? a?.apartment_name ?? a?.number ?? a?.apartment_number ?? "";
  return String(v).trim();
};

export default function Dashboard() {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    total_income: 0,
    monthly_income: 0,
    pending_payments: 0,
    unpaid_month_total: 0,
    total_deductions: 0,
    total_tenants: 0,
    total_apartments: 0,
    apartment_numbers: [],
  });

  const [waterLight, setWaterLight] = useState({
    total_luce: 0,
    total_acqua: 0,
    total: 0,
  });

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [waterLightSelected, setWaterLightSelected] = useState({
    total_luce: 0,
    total_acqua: 0,
    total: 0,
    total_deductions: 0,
  });
  const [selectedError, setSelectedError] = useState("");
  const [globalError, setGlobalError] = useState("");

  const [parkingInfo, setParkingInfo] = useState({
    total_parking: 0,
    parking_numbers: [],
  });

  const [contractAlerts, setContractAlerts] = useState([]);

  const getContractAlert = (tenant) => {
    if (!tenant?.end_date || tenant.is_active === false) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = new Date(`${tenant.end_date}T00:00:00`);
    if (Number.isNaN(endDate.getTime())) return null;

    const daysLeft = Math.ceil((endDate - today) / 86400000);

    if (daysLeft > 60) return null;

    return {
      ...tenant,
      days_left: daysLeft,
    };
  };

  const renewContract = async (tenant) => {
    setGlobalError("");

    const newEndDate = window.prompt(
      `Vendos datën e re të mbarimit të kontratës për ${tenant.name} (YYYY-MM-DD):`,
      tenant.end_date || ""
    );

    if (newEndDate === null) return;

    const trimmedDate = newEndDate.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
      setGlobalError("Data duhet të jetë në formatin YYYY-MM-DD.");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/tenants/${tenant.id}/renew`, {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ end_date: trimmedDate }),
      });

      if (res.status === 401 || res.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await res.json();
      if (!res.ok) {
        setGlobalError(data?.error || "Gabim gjatë rinovimit të kontratës.");
        return;
      }

      await fetchStats();
    } catch {
      setGlobalError("Nuk u arrit lidhja me serverin.");
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const handleUnauthorized = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    navigate("/login");
  };

  useEffect(() => {
    fetchStats();
    fetchWaterLightCurrentMonth();
    fetchParkingInfo();
  }, []);

  useEffect(() => {
    fetchWaterLightByMonth(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear]);

  const fetchStats = async () => {
    setGlobalError("");

    let s = {};
    try {
      const r = await fetch(`${API_URL}/payments/stats`, {
        headers: getAuthHeaders(),
      });

      if (r.status === 401 || r.status === 422) {
        handleUnauthorized();
        return;
      }

      if (r.ok) {
        s = await r.json();
      }
    } catch (e) {
      console.error("Gabim te /payments/stats:", e);
      setGlobalError("Gabim gjatë ngarkimit të statistikave.");
    }

    let tenants = [];
    try {
      const r = await fetch(`${API_URL}/tenants/`, {
        headers: getAuthHeaders(),
      });

      if (r.status === 401 || r.status === 422) {
        handleUnauthorized();
        return;
      }

      if (r.ok) {
        tenants = await r.json();
      }
    } catch (e) {
      console.error("Gabim te /tenants:", e);
      setGlobalError("Gabim gjatë ngarkimit të të dhënave.");
    }

    const alerts = (Array.isArray(tenants) ? tenants : [])
      .map(getContractAlert)
      .filter(Boolean)
      .sort((a, b) => a.days_left - b.days_left);

    setContractAlerts(alerts);

    let apartments = [];
    try {
      const r = await fetch(`${API_URL}/apartments/`, {
        headers: getAuthHeaders(),
      });

      if (r.status === 401 || r.status === 422) {
        handleUnauthorized();
        return;
      }

      if (r.ok) {
        apartments = await r.json();
      }
    } catch (e) {
      console.error("Gabim te /apartments:", e);
      setGlobalError("Gabim gjatë ngarkimit të apartamenteve.");
    }

    const apartmentNumbers = Array.isArray(apartments)
      ? apartments.map(getApartmentNumber).filter((x) => x !== "")
      : [];

    const sortKey = (v) => {
      const s = String(v).trim();
      return /^\d+$/.test(s) ? [0, parseInt(s, 10)] : [1, s.toLowerCase()];
    };

    const apartmentNumbersSorted = [...new Set(apartmentNumbers)].sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      if (ka[0] !== kb[0]) return ka[0] - kb[0];
      return ka[1] < kb[1] ? -1 : ka[1] > kb[1] ? 1 : 0;
    });

    setStats({
      ...s,
      total_income: Number(s.total_income || 0),
      monthly_income: Number(s.monthly_income || 0),
      pending_payments: Number(s.pending_payments || 0),
      unpaid_month_total: Number(s.unpaid_month_total || 0),
      total_deductions: Number(s.total_deductions || 0),
      total_tenants: Array.isArray(tenants) ? tenants.filter((t) => t.is_active !== false).length : 0,
      total_apartments: Array.isArray(apartments) ? apartments.length : 0,
      apartment_numbers: apartmentNumbersSorted,
    });
  };

  const fetchParkingInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/tenants/parking`, {
        headers: getAuthHeaders(),
      });

      if (res.status === 401 || res.status === 422) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) return;

      const data = await res.json();
      setParkingInfo({
        total_parking: Number(data?.total_parking || 0),
        parking_numbers: Array.isArray(data?.parking_numbers)
          ? data.parking_numbers
          : [],
      });
    } catch (e) {
      console.error("Gabim te /tenants/parking:", e);
      setParkingInfo({ total_parking: 0, parking_numbers: [] });
    }
  };

  const fetchWaterLightCurrentMonth = async () => {
    const month = getCurrentMonth();
    const year = CURRENT_YEAR;

    try {
      const res = await fetch(
        `${API_URL}/payments/water-light-totals?month=${encodeURIComponent(month)}&year=${year}`,
        { headers: getAuthHeaders() }
      );

      if (res.status === 401 || res.status === 422) {
        handleUnauthorized();
        return;
      }

      if (res.ok) {
        const data = await res.json();
        setWaterLight({
          total_luce: Number(data.total_luce || 0),
          total_acqua: Number(data.total_acqua || 0),
          total: Number(data.total || 0),
        });
      } else {
        setWaterLight({ total_luce: 0, total_acqua: 0, total: 0 });
      }
    } catch (e) {
      setWaterLight({ total_luce: 0, total_acqua: 0, total: 0 });
    }
  };

  const fetchWaterLightByMonth = async (month, year) => {
    setSelectedError("");

    try {
      const res = await fetch(
        `${API_URL}/payments/water-light-totals?month=${encodeURIComponent(month)}&year=${year}`,
        { headers: getAuthHeaders() }
      );

      if (res.status === 401 || res.status === 422) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setWaterLightSelected({
          total_luce: 0,
          total_acqua: 0,
          total: 0,
          total_deductions: 0
        });
        setSelectedError(err?.error || `Nuk ka pagesa për ${month} ${year}`);
        return;
      }

      const data = await res.json();
      setWaterLightSelected({
        total_luce: Number(data.total_luce || 0),
        total_acqua: Number(data.total_acqua || 0),
        total: Number(data.total || 0),
        total_deductions: Number(data.total_deductions || 0),
      });
    } catch (e) {
      setWaterLightSelected({
        total_luce: 0,
        total_acqua: 0,
        total: 0,
        total_deductions: 0
      });
      setSelectedError("Gabim gjatë leximit të të dhënave.");
    }
  };

  return (
    <div className="dashboard-container">
      <h2 className="dashboard-title">Dashboard</h2>

      {globalError && (
        <div style={{ marginBottom: 15, color: "#b00020", fontWeight: 600 }}>
          {globalError}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card green">
          <h3>Totali i të ardhurave</h3>
          <p>{Number(stats.total_income || 0).toLocaleString()} Lek</p>
        </div>

        <div className="stat-card blue">
          <h3>Të ardhurat ({getCurrentMonth()} {CURRENT_YEAR})</h3>
          <p>{Number(stats.monthly_income || 0).toLocaleString()} Lek</p>
        </div>

        <div className="stat-card orange">
          <h3>Shuma e papaguar ({getCurrentMonth()} {CURRENT_YEAR})</h3>
          <p>{Number(stats.unpaid_month_total || 0).toLocaleString()} Lek</p>
        </div>

        <div className="stat-card red">
          <h3>Pagesa në pritje ({getCurrentMonth()} {CURRENT_YEAR})</h3>
          <p>{Number(stats.pending_payments || 0)}</p>
        </div>

        <div className="stat-card" style={{ borderLeft: "4px solid #7c3aed" }}>
          <h3>Zbritje ({getCurrentMonth()} {CURRENT_YEAR})</h3>
          <p style={{ color: "#7c3aed" }}>
            - {Number(stats.total_deductions || 0).toLocaleString()} Lek
          </p>
        </div>

        <div className="stat-card purple">
          <h3>Qiramarrës</h3>
          <p>{Number(stats.total_tenants || 0)}</p>
        </div>

        <div className="stat-card dark">
          <h3>Apartamente</h3>
          <p>{Number(stats.total_apartments || 0)}</p>
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>
            Numrat:{" "}
            {stats.apartment_numbers && stats.apartment_numbers.length > 0
              ? stats.apartment_numbers.join(", ")
              : "—"}
          </div>
        </div>

        <div className="stat-card pink small-text">
          <h3>Parking</h3>
          <div style={{ marginTop: 6 }}>
            Totali: <b>{Number(parkingInfo.total_parking || 0)}</b>
          </div>
          <div style={{ marginTop: 6 }}>
            Numra unikë: <b>{parkingInfo.parking_numbers.length}</b>
          </div>
          <div style={{ marginTop: 8 }}>
            Numrat:{" "}
            {parkingInfo.parking_numbers.length > 0
              ? parkingInfo.parking_numbers.join(", ")
              : "—"}
          </div>
        </div>

        <div className="stat-card">
          <h3>Ujë & Dritë ({getCurrentMonth()} {CURRENT_YEAR})</h3>
          <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, lineHeight: 1.7 }}>
            <div>Drita: {Number(waterLight.total_luce || 0).toLocaleString()} Lek</div>
            <div>Uji: {Number(waterLight.total_acqua || 0).toLocaleString()} Lek</div>
            <div>Totali: {Number(waterLight.total || 0).toLocaleString()} Lek</div>
          </div>
        </div>
      </div>

      {contractAlerts.length > 0 && (
        <div className="stat-card" style={{ marginTop: 20 }}>
          <h3>Njoftime për kontratat</h3>

          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            {contractAlerts.map((tenant) => {
              const expired = tenant.days_left < 0;
              const message = expired
                ? `Kontrata ka skaduar prej ${Math.abs(tenant.days_left)} ditësh.`
                : tenant.days_left === 0
                ? "Kontrata skadon sot."
                : `Kontrata skadon pas ${tenant.days_left} ditësh.`;

              return (
                <div
                  key={tenant.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                    padding: "12px 14px",
                    borderRadius: 8,
                    background: expired ? "rgba(239,68,68,0.22)" : "rgba(245,158,11,0.22)",
                    border: expired
                      ? "1px solid rgba(239,68,68,0.6)"
                      : "1px solid rgba(245,158,11,0.6)",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700 }}>
                      ⚠ {tenant.name} — Apt {tenant.apartment_name || "-"}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 14 }}>
                      {message} Mbarimi: {tenant.end_date}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => renewContract(tenant)}
                    style={{
                      padding: "9px 12px",
                      border: "none",
                      borderRadius: 7,
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    Rinovo kontratën
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="stat-card" style={{ marginTop: 20 }}>
        <h3>Ujë & Dritë + Zbritje (zgjedh muajin dhe vitin)</h3>

        <div style={{ display: "flex", gap: 10, marginTop: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {MONTHS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {selectedError ? (
          <p style={{ color: "crimson", fontSize: 14, fontWeight: 600 }}>
            {selectedError}
          </p>
        ) : (
          <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.9 }}>
            <div>Drita: {Number(waterLightSelected.total_luce || 0).toLocaleString()} Lek</div>
            <div>Uji: {Number(waterLightSelected.total_acqua || 0).toLocaleString()} Lek</div>
            <div>Totali ujë+dritë: {Number(waterLightSelected.total || 0).toLocaleString()} Lek</div>
            {waterLightSelected.total_deductions > 0 && (
              <div style={{ color: "#7c3aed", marginTop: 4 }}>
                Zbritje totale: - {Number(waterLightSelected.total_deductions || 0).toLocaleString()} Lek
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
